import React, { MouseEvent } from 'react';
import { Modal } from 'antd';
import { ManualBilling } from '../../hooks/useManualBilling/useManualBilling';
import moment, { Moment } from 'moment';
import { Input, DatePicker } from 'components';
import { useFormik } from 'formik';
import styles from './styles.module.scss';
import { manualBillingValidationSchema, ManualBillingFormValues } from './validation';

interface ManualBillingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: Omit<ManualBilling, 'Id'>) => Promise<void>;
  initialValues?: ManualBilling;
  teamId: number;
  userId: number;
}

export const ManualBillingModal: React.FC<ManualBillingModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialValues,
  teamId,
  userId,
}) => {
  const formik = useFormik<ManualBillingFormValues>({
    initialValues: {
      Details: initialValues?.Details || '',
      Amount: initialValues?.Amount?.toString() || '',
      Card: initialValues?.Card || '',
      BillingDate: initialValues?.BillingDate ? moment(initialValues.BillingDate) : null,
    },
    validationSchema: manualBillingValidationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        await onSubmit({
          Details: values.Details,
          Amount: Number(values.Amount),
          Card: values.Card,
          BillingDate: values.BillingDate ? values.BillingDate.toISOString() : '',
          TeamId: teamId,
          UserId: userId,
        });
        formik.resetForm();
        onClose();
      } catch (error) {
        console.error('Submission failed:', error);
      }
    },
  });

  const handleDateChange = (e: { target: { value: Date[] } } | undefined) => {
    const date = e?.target?.value?.[0];
    formik.setFieldValue('BillingDate', date ? moment(date) : null);
    formik.setFieldTouched('BillingDate', true);
  };

  const handleOk = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    formik.handleSubmit();
  };

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      onOk={handleOk}
      title="Add Manual Billing"
      width={500}
      className={styles.manualBillingModal}
      maskClosable={false}
      bodyStyle={{ padding: 0 }}
      okButtonProps={{
        disabled: !formik.isValid || !formik.dirty,
        type: 'primary',
      }}
      okText="Save"
    >
      <form onSubmit={formik.handleSubmit}>
        <div className={styles.formContainer}>
          <div style={{ fontSize: '18px' }}>
            <label htmlFor="Details">Details*</label>
            <Input
              id="Details"
              name="Details"
              variant={Input.LIGHT}
              placeholder="Enter details"
              value={formik.values.Details}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.Details && formik.errors.Details && (
              <div className={styles.error}>{formik.errors.Details}</div>
            )}
          </div>

          <div style={{ fontSize: '18px' }}>
            <label htmlFor="Amount">Amount*</label>
            <Input
              id="Amount"
              name="Amount"
              variant={Input.LIGHT}
              type="number"
              placeholder="Enter amount"
              value={formik.values.Amount}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.Amount && formik.errors.Amount && (
              <div className={styles.error}>{formik.errors.Amount}</div>
            )}
          </div>

          <div style={{ fontSize: '18px' }}>
            <label htmlFor="Card">Card*</label>
            <Input
              id="Card"
              name="Card"
              variant={Input.LIGHT}
              placeholder="Enter card details"
              value={formik.values.Card}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.Card && formik.errors.Card && (
              <div className={styles.error}>{formik.errors.Card}</div>
            )}
          </div>

          <div style={{ fontSize: '18px' }}>
            <label htmlFor="BillingDate">Billing Date*</label>
            <DatePicker
              id="BillingDate"
              name="BillingDate"
              variant={DatePicker.ROUNDED}
              options={{ enableTime: false }}
              format="m/d/Y"
              placeholder="MM/DD/YYYY"
              allowTypedInput={true}
              allowPlugin={false}
              value={formik.values.BillingDate?.toDate()}
              onChange={handleDateChange}
            />
            {formik.touched.BillingDate && formik.errors.BillingDate && (
              <div className={styles.error}>{formik.errors.BillingDate}</div>
            )}
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default ManualBillingModal;
